import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  Stack,
  TablePagination,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";

function stripHtml(value) {
  return String(value || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Modal to browse / manage questions pinned on a created quiz.
 * Add & remove only change quiz refs — bank originals are never deleted.
 */
export default function QuizQuestionsModal({
  open,
  onClose,
  quizTitle = "",
  questions = [],
  requested = 0,
  poolTotal = 0,
  picked = 0,
  loading = false,
  error = "",
  saving = false,
  onAddQuestions,
  onRemoveQuestion,
  onReload,
  onEditQuiz,
}) {
  const theme = useTheme();
  const darkMode = theme.palette.mode === "dark";
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  useEffect(() => {
    if (!open) return;
    setSearch("");
    setPage(0);
  }, [open, quizTitle]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return questions;
    return questions.filter((question) => {
      const body = stripHtml(question.question_content).toLowerCase();
      const bank = String(question.bank || "").toLowerCase();
      const difficulty = String(question.difficulty || "").toLowerCase();
      const answers = (question.answers || [])
        .map((a) => stripHtml(a.text).toLowerCase())
        .join(" ");
      return (
        body.includes(q) ||
        bank.includes(q) ||
        difficulty.includes(q) ||
        answers.includes(q)
      );
    });
  }, [questions, search]);

  useEffect(() => {
    setPage(0);
  }, [search]);

  const pageSlice = useMemo(() => {
    const start = page * rowsPerPage;
    return filtered.slice(start, start + rowsPerPage);
  }, [filtered, page, rowsPerPage]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          bgcolor: "background.card",
          color: theme.palette.text.main,
          maxHeight: "90vh",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 1,
          pr: 1.5,
          pb: 1,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h6" fontWeight={700} noWrap title={quizTitle}>
            {quizTitle || "Quiz questions"}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {loading
              ? "Loading questions…"
              : `${picked || questions.length} pinned · ${poolTotal || "—"} in pool · asked for ${requested || "—"}`}
          </Typography>
        </Box>
        <Stack direction="row" spacing={0.5} alignItems="center">
          {onAddQuestions ? (
            <Tooltip title="Add questions from bank">
              <span>
                <IconButton
                  size="small"
                  onClick={onAddQuestions}
                  disabled={loading || saving}
                  aria-label="Add questions"
                  sx={{ color: theme.palette.info.main }}
                >
                  <AddRoundedIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          ) : null}
          {onReload ? (
            <Tooltip title="Reload">
              <span>
                <IconButton
                  size="small"
                  onClick={onReload}
                  disabled={loading || saving}
                  aria-label="Reload questions"
                >
                  {loading ? <CircularProgress size={16} /> : <RefreshRoundedIcon fontSize="small" />}
                </IconButton>
              </span>
            </Tooltip>
          ) : null}
          <IconButton size="small" onClick={onClose} aria-label="Close">
            <CloseRoundedIcon fontSize="small" />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent dividers sx={{ pt: 2 }}>
        <TextField
          size="small"
          fullWidth
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search questions, answers, bank…"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchRoundedIcon fontSize="small" sx={{ color: "text.secondary" }} />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 2 }}
        />

        {error ? (
          <Typography variant="body2" color="error" sx={{ mb: 1.5 }}>
            {error}
          </Typography>
        ) : null}

        {loading && !questions.length ? (
          <Box py={5} display="flex" justifyContent="center">
            <CircularProgress size={28} />
          </Box>
        ) : null}

        {!loading && !error && !filtered.length ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
            {search.trim()
              ? "No questions match your search."
              : "No questions pinned on this quiz yet."}
          </Typography>
        ) : null}

        <Stack
          spacing={1.5}
          divider={<Divider flexItem sx={{ opacity: 0.45 }} />}
          sx={{
            p: 1.25,
            borderRadius: 2,
            border: `1px solid ${alpha(theme.palette.divider, 0.7)}`,
            bgcolor: darkMode ? alpha("#fff", 0.02) : alpha(theme.palette.info.main, 0.03),
          }}
        >
          {pageSlice.map((question, index) => {
            const absoluteIndex = page * rowsPerPage + index;
            const body = stripHtml(question.question_content);
            const canRemove = Boolean(onRemoveQuestion);
            return (
              <Box key={`${question.bank}-${question.id}-${absoluteIndex}`}>
                <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" spacing={1} alignItems="center" mb={0.5} flexWrap="wrap">
                      <Typography variant="caption" fontWeight={700} color="text.secondary">
                        Q{absoluteIndex + 1}
                      </Typography>
                      <Chip
                        size="small"
                        label={question.bank === "microlab" ? "Microlab bank" : "Private bank"}
                        sx={{ height: 22, fontWeight: 600 }}
                      />
                      {question.difficulty ? (
                        <Chip
                          size="small"
                          variant="outlined"
                          label={String(question.difficulty)}
                          sx={{ height: 22, textTransform: "capitalize" }}
                        />
                      ) : null}
                    </Stack>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                      {body || "Untitled question"}
                    </Typography>
                    <Stack spacing={0.35}>
                      {(question.answers || []).map((answer) => (
                        <Typography
                          key={`${question.id}-${answer.key}`}
                          variant="caption"
                          sx={{
                            color: answer.is_correct
                              ? theme.palette.success.main
                              : "text.secondary",
                            fontWeight: answer.is_correct ? 700 : 500,
                          }}
                        >
                          {String(answer.key || "").toUpperCase()}. {stripHtml(answer.text)}
                          {answer.is_correct ? " ✓" : ""}
                        </Typography>
                      ))}
                    </Stack>
                  </Box>
                  {canRemove ? (
                    <Tooltip title="Remove from this quiz only (bank original kept)">
                      <span>
                        <IconButton
                          size="small"
                          aria-label={`Remove question ${absoluteIndex + 1}`}
                          disabled={saving}
                          onClick={() => onRemoveQuestion(question)}
                          sx={{ color: theme.palette.error.main }}
                        >
                          <DeleteOutlineRoundedIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  ) : null}
                </Stack>
              </Box>
            );
          })}
        </Stack>

        <TablePagination
          component="div"
          count={filtered.length}
          page={page}
          onPageChange={(_, next) => setPage(next)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25]}
          labelRowsPerPage="Per page:"
          sx={{ color: theme.palette.text.main, mt: 1 }}
        />
      </DialogContent>

      <DialogActions sx={{ px: 2.5, py: 1.5, gap: 1, flexWrap: "wrap" }}>
        {onAddQuestions ? (
          <Button
            variant="contained"
            color="info"
            startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <AddRoundedIcon />}
            onClick={onAddQuestions}
            disabled={loading || saving}
            sx={{ textTransform: "none", fontWeight: 700, mr: "auto" }}
          >
            Add questions
          </Button>
        ) : onEditQuiz ? (
          <Button
            variant="outlined"
            color="info"
            startIcon={<EditRoundedIcon />}
            onClick={onEditQuiz}
            sx={{ textTransform: "none", fontWeight: 600, mr: "auto" }}
          >
            Edit quiz settings
          </Button>
        ) : (
          <Box sx={{ flex: 1 }} />
        )}
        {onAddQuestions && onEditQuiz ? (
          <Button
            variant="outlined"
            color="info"
            startIcon={<EditRoundedIcon />}
            onClick={onEditQuiz}
            disabled={saving}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            Edit quiz settings
          </Button>
        ) : null}
        <Button onClick={onClose} disabled={saving} sx={{ textTransform: "none", fontWeight: 600 }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

QuizQuestionsModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  quizTitle: PropTypes.string,
  questions: PropTypes.array,
  requested: PropTypes.number,
  poolTotal: PropTypes.number,
  picked: PropTypes.number,
  loading: PropTypes.bool,
  error: PropTypes.string,
  saving: PropTypes.bool,
  onAddQuestions: PropTypes.func,
  onRemoveQuestion: PropTypes.func,
  onReload: PropTypes.func,
  onEditQuiz: PropTypes.func,
};
